import type { HomeRecentMergedEntry } from './homeRecentMerge';
import { getProcessMethodKind } from './processTypeUtils';
import { computeHomeProcessDisplayStatus, type HomeProcessDisplayStatus } from './processHomeStatus';

export type MyProcessViewMode = 'grid' | 'list';
export type MyProcessSort = 'date_desc' | 'date_asc';
export type MyProcessStatusFilter = 'all' | 'completed' | 'ongoing';
export type MyProcessOwnerFilter = 'all' | 'me' | 'other';
export type MyProcessTypeFilter = 'all' | 'AI' | 'DATA_COLLECT' | 'HUMAN' | 'IMAGE' | 'MANUAL';

export const MY_PROCESSES_VIEW_STORAGE_KEY = 'famla.myProcesses.viewMode';

export type MyProcessLibraryFilters = {
    status: MyProcessStatusFilter;
    owner: MyProcessOwnerFilter;
    type: MyProcessTypeFilter;
};

export const DEFAULT_MY_PROCESS_FILTERS: MyProcessLibraryFilters = {
    status: 'all',
    owner: 'all',
    type: 'all',
};

export function readStoredViewMode(): MyProcessViewMode {
    try {
        const raw = localStorage.getItem(MY_PROCESSES_VIEW_STORAGE_KEY);
        if (raw === 'list' || raw === 'grid') return raw;
    } catch {
        /* ignore */
    }
    return 'grid';
}

export function storeViewMode(mode: MyProcessViewMode): void {
    try {
        localStorage.setItem(MY_PROCESSES_VIEW_STORAGE_KEY, mode);
    } catch {
        /* ignore */
    }
}

function isOwnerMe(
    entry: HomeRecentMergedEntry,
    ctx: { userId?: string | null; userFullname?: string | null },
): boolean {
    const userId = (ctx.userId || '').trim();
    const userFullname = (ctx.userFullname || '').trim();

    if (entry.kind === 'process') {
        const ownerId = String(entry.item.owner?._id || '').trim();
        if (userId && ownerId) return ownerId === userId;
        return Boolean(userFullname && entry.item.owner?.fullname === userFullname);
    }

    if (entry.item.opened_as_contributor || entry.item.is_collection_co_owner) return false;
    if (entry.item.is_primary_owner === true) return true;
    const ownerId = String(entry.item.owner_id || '').trim();
    if (userId && ownerId) return ownerId === userId;
    return false;
}

function resolveDisplayStatus(
    entry: HomeRecentMergedEntry,
    completedByProcessId: Record<string, boolean | null | undefined>,
    notifProcess: Record<string, boolean>,
): HomeProcessDisplayStatus {
    if (entry.kind === 'dataCollect') {
        const s = String(entry.item.status || '').toLowerCase();
        if (s === 'closed') return 'completed';
        if (s === 'draft') return 'new';
        return 'ongoing';
    }
    const hasNotif = Boolean(notifProcess[entry.item._id]);
    const done = completedByProcessId[entry.item._id];
    return computeHomeProcessDisplayStatus(hasNotif, done === undefined ? null : done);
}

function matchesStatusFilter(
    displayStatus: HomeProcessDisplayStatus,
    filter: MyProcessStatusFilter,
): boolean {
    if (filter === 'all') return true;
    if (filter === 'completed') return displayStatus === 'completed';
    // ongoing includes "new" (not finished yet)
    return displayStatus === 'ongoing' || displayStatus === 'new';
}

function matchesTypeFilter(entry: HomeRecentMergedEntry, filter: MyProcessTypeFilter): boolean {
    if (filter === 'all') return true;
    if (filter === 'DATA_COLLECT') return entry.kind === 'dataCollect';
    if (entry.kind !== 'process') return false;
    return getProcessMethodKind(entry.item.type) === filter;
}

export function filterAndSortMyProcesses(
    entries: HomeRecentMergedEntry[],
    opts: {
        filters: MyProcessLibraryFilters;
        sort: MyProcessSort;
        completedByProcessId: Record<string, boolean | null | undefined>;
        notifProcess: Record<string, boolean>;
        userId?: string | null;
        userFullname?: string | null;
    },
): HomeRecentMergedEntry[] {
    const { filters, sort, completedByProcessId, notifProcess, userId, userFullname } = opts;

    const filtered = entries.filter((entry) => {
        if (!matchesTypeFilter(entry, filters.type)) return false;

        const mine = isOwnerMe(entry, { userId, userFullname });
        if (filters.owner === 'me' && !mine) return false;
        if (filters.owner === 'other' && mine) return false;

        const status = resolveDisplayStatus(entry, completedByProcessId, notifProcess);
        if (!matchesStatusFilter(status, filters.status)) return false;

        return true;
    });

    const sorted = [...filtered].sort((a, b) =>
        sort === 'date_desc' ? b.sortKey - a.sortKey : a.sortKey - b.sortKey,
    );
    return sorted;
}

export function countActiveMyProcessFilters(filters: MyProcessLibraryFilters): number {
    let n = 0;
    if (filters.status !== 'all') n += 1;
    if (filters.owner !== 'all') n += 1;
    if (filters.type !== 'all') n += 1;
    return n;
}
